import type { QuickFilterAction } from '@unifyapps/defs/types/blocks';
import { FilterActionsEnum } from '@unifyapps/defs/types/blocks';
import useToggle from '@unifyapps/hooks/useToggle';
import SvgChevronDown from '@unifyapps/icons/outline/ChevronDown';
import type { ToolbarVariant } from '@unifyapps/table/types';
import { Box } from '@unifyapps/ui/components/Box';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QuickFilters as BaseQuickFilters } from '../../../filter/components/QuickFilters';
import type { FiltersType } from '../../../filter/types';
import { DropdownButtonGroup } from '../../DropdownButtonGroup';

type Props = {
  setFilter: Dispatch<SetStateAction<string | undefined>>;
  filterState: FiltersType;
  filterOptions: { id: string; name: string }[];
  filter?: string;
  style?: React.CSSProperties;
  toolbarVariant?: ToolbarVariant;
};

export function QuickFilters({
  setFilter,
  filterState,
  filterOptions,
  filter,
  style,
  toolbarVariant,
}: Props) {
  const quickFiltersContainerRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, { on, off }] = useToggle(false);

  const quickFilterActions = useMemo<QuickFilterAction[]>(
    () =>
      filterOptions.map(({ id, name }) => ({
        type: FilterActionsEnum.QuickFilter,
        filterType: FilterActionsEnum.DataFilter,
        id,
        label: name,
        appearance: {
          size: 'sm',
        },
      })),
    [filterOptions],
  );

  useEffect(() => {
    if (quickFiltersContainerRef.current) {
      if (
        !isOverflowing &&
        quickFiltersContainerRef.current.scrollWidth > quickFiltersContainerRef.current.clientWidth
      ) {
        on();
      } else {
        off();
      }
    }
  }, [on, off, isOverflowing]);

  const setFilters = useCallback(
    ({ dataQuickFilter }: FiltersType) => {
      setFilter(dataQuickFilter?.id);
    },
    [setFilter],
  );

  const items = useMemo(() => {
    return quickFilterActions.map((action) => ({
      type: 'label' as const,
      id: action.id,
      title: action.label ?? action.id,
    }));
  }, [quickFilterActions]);

  const selectedFilterLabel =
    quickFilterActions.find((action) => action.id === filter)?.label ?? filter;

  return !isOverflowing ? (
    <Box className="gap-md flex min-w-0 flex-row" ref={quickFiltersContainerRef}>
      <BaseQuickFilters
        filterState={filterState}
        quickFilterActions={quickFilterActions}
        setFilters={setFilters}
        style={style}
        toolbarVariant={toolbarVariant}
      />
    </Box>
  ) : (
    <DropdownButtonGroup
      color="neutral"
      endDecoratorComponent={SvgChevronDown}
      items={items}
      menuListSize="sm"
      onAction={setFilter}
      size="sm"
      variant="soft"
    >
      {selectedFilterLabel}
    </DropdownButtonGroup>
  );
}
